import { useEffect, useSyncExternalStore } from "react";
import { getTokenAsync } from "@coworker/app/src/hooks/API/getAuthToken";
import useFlag, { FLAGS } from "@coworker/app/src/hooks/useFlag";
import { useOnline } from "@coworker/app/src/core/hooks/useOnline";
import { NotificationMessages } from "../service/NotificationMessages";
import { notificationMessagesCache } from "../service/NotificationMessagesCache";
import { isTaskNotificationEnabled } from "../components/task-notification/utils/task-notification-filter";

export const useNotificationMessages = (storeId: string, uid: string) => {
  const { isOnline, isVisible = true } = useOnline();
  const enableTaskSeriesNotifications = useFlag(
    FLAGS.ENABLE_TASK_SERIES_NOTIFICATIONS
  );

  // Open a connection to the server and perform a long-lived REST GET request to receive
  // server-sent events. Received messages are inserted into the message cache.
  // The connection is closed when the tab is hidden (mobile app switch / screen lock)
  // and re-established when the tab becomes visible again.
  useEffect(() => {
    if (isOnline && isVisible && storeId && uid) {
      const notificationMessages = new NotificationMessages(
        storeId,
        uid,
        getTokenAsync
      );
      notificationMessages.open();

      return () => {
        notificationMessages.close();
      };
    } else {
      return;
    }
  }, [isOnline, isVisible, storeId, uid]);

  // The message cache where the messages are inserted.
  const notifications = useSyncExternalStore(
    (listener: () => void) =>
      notificationMessagesCache.subscribe(listener, storeId, uid),
    () => notificationMessagesCache.getMessagesForStoreAndUid(storeId, uid)
  );

  return notifications.filter((notification) => {
    if (notification.contentType !== "task-notification") return true;
    return isTaskNotificationEnabled(
      notification.content.type,
      enableTaskSeriesNotifications
    );
  });
};
